import { Button, makeStyles, Spinner } from "@fluentui/react-components";
import { ArrowLeftFilled } from "@fluentui/react-icons";
import { useQueryClient } from "@skanska/vsa-client-auth";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ToolForm from "../components/tool/ToolForm";
import {
  useGetDocumentFields,
  useGetLinkFields,
  useGetToolDraft,
  useUpdateToolDraft,
} from "../hooks/useTools";
import { ClientError } from "../models/ClientError";
import { ToolFormData, ToolTypes } from "../models/Tool";
import { validateFormFieldsForRequest } from "../services/FieldService";
import { getToolType } from "../services/ToolService";

const useClasses = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
  },
  buttons: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  title: {
    paddingLeft: "5px",
  },
});

export const EditToolPage: React.FC = () => {
  const { t } = useTranslation();
  const classes = useClasses();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const autoOpenPublishDialog = Boolean(
    (location.state as { openPublishOnLoad?: boolean } | null)
      ?.openPublishOnLoad
  );

  const hasClearedPublishIntent = useRef(false);

  useEffect(() => {
    if (autoOpenPublishDialog && !hasClearedPublishIntent.current) {
      hasClearedPublishIntent.current = true;
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [autoOpenPublishDialog, location.pathname, navigate]);

  if (!id) {
    const missingIdError: ClientError = {
      message: "Tool id is missing",
      name: "Tool id is missing",
    };

    throw missingIdError;
  }
  const idString = id as string;

  const { mutateAsync: updateTool, isPending } = useUpdateToolDraft(idString);

  const { data: tool, isLoading, isRefetching } = useGetToolDraft(idString);

  const toolType = tool ? getToolType(tool) : undefined;

  const { data: linkFields, isLoading: linkFieldsLoading } = useGetLinkFields();
  const { data: documentFields, isLoading: documentFieldsLoading } =
    useGetDocumentFields();

  const viewDefinition =
    toolType === ToolTypes.Document ? documentFields : linkFields;
  const viewDefinitionLoading =
    toolType === ToolTypes.Document ? documentFieldsLoading : linkFieldsLoading;

  async function onFormSubmit(formData: ToolFormData) {
    if (!viewDefinition) {
      return;
    }
    const requestFields = validateFormFieldsForRequest(
      formData.fieldsWithValues,
      viewDefinition.fields
    );
    const formDataToSend = new FormData();

    formDataToSend.append("VersionId", idString);
    formDataToSend.append("AdminDisplayName", formData.adminDisplayName || "");
    formDataToSend.append("Title", formData.title);
    if (formData.toolType) {
      formDataToSend.append("ToolType", formData.toolType);
    }
    if (formData.file) {
      formDataToSend.append("NewFile", formData.file);
    }
    if (formData.fileName) {
      formDataToSend.append("FileName", formData.fileName);
    }
    if (formData.linkUrl) {
      formDataToSend.append("LinkUrl", formData.linkUrl);
    }
    formDataToSend.append("FieldsWithValues", JSON.stringify(requestFields));

    await updateTool(formDataToSend);

    await queryClient.refetchQueries({
      queryKey: [`tool-${idString}`],
      exact: true,
    });
  }

  return (
    <div className={classes.root}>
      <h1 className={classes.title}>{t("editTool")}</h1>

      {!isLoading ? (
        <ToolForm
          existingTool={tool}
          isLoading={isPending || viewDefinitionLoading}
          onSubmit={onFormSubmit}
          toolFields={viewDefinition?.fields}
          isRefetching={isRefetching}
          autoOpenPublishDialog={autoOpenPublishDialog}
        />
      ) : (
        <Spinner />
      )}
      <div className={classes.buttons}>
        <Button
          icon={<ArrowLeftFilled />}
          size="small"
          onClick={() => navigate(-1)}
        >
          {t("back")}
        </Button>
      </div>
    </div>
  );
};

export default EditToolPage;
